import {
  GetAllTeamsForUserManagementQuery,
  useGetAllTeamsForUserManagementQuery,
} from '@/__generated__/graphql';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavigationListItem } from '@/types';
import { NavigationPanel } from '../shared/NavigationPanel';
import { PageLoader } from '@/components/shared/PageLoader';
import { PanelBox } from '../styles';
import { TeamDetail } from './TeamDetail';

type Team = GetAllTeamsForUserManagementQuery['allTeams'][0];

export const TeamsPanel = () => {
  const [selectedTeam, setSelectedTeam] = useState<Team | undefined>(undefined);
  const { leftDrawerWidth } = useDrawerWidths();
  const { data, loading } = useGetAllTeamsForUserManagementQuery();

  const teams = useMemo(() => {
    return data?.allTeams ?? [];
  }, [data]);

  useEffect(() => {
    if (!loading && teams?.length) {
      const updatedSelectedTeam = teams.find((team) => team.id === selectedTeam?.id);
      setSelectedTeam(updatedSelectedTeam || teams[0]);
    } else {
      setSelectedTeam(undefined);
    }
  }, [loading, setSelectedTeam, selectedTeam, teams]);

  const handleTeamClick = useCallback((team: Team) => setSelectedTeam(team), [setSelectedTeam]);

  const items = useMemo(() => {
    return teams.map((team) => {
      const item: NavigationListItem = {
        id: team.id,
        value: team.name,
        label: team.name,
        count: team.users.length,
        handleClick: () => {
          handleTeamClick(team);
        },
      };
      return item;
    });
  }, [handleTeamClick, teams]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '107px' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Stack direction="row" height="100%">
      <NavigationPanel
        itemType="Team"
        items={items}
        selectedItemId={selectedTeam?.id ?? ''}
        showCount
        searchDisabled
        showTotalHeader
      />
      <PanelBox width={`calc(100vw - ${leftDrawerWidth}px)`}>
        <TeamDetail loading={loading} selectedTeam={selectedTeam} />
      </PanelBox>
    </Stack>
  );
};
